import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { channelPartnerAuthAPI } from '../../services/api';
import { Card, Button, Spinner, Badge, EmptyState, PageHeader } from '../../components/UI';
import { HiOutlineCalendar, HiOutlinePhone } from 'react-icons/hi';

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const STATUS_LABEL = { pending: 'Pending', scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled' };
const STATUS_VARIANT = { pending: 'warning', scheduled: 'info', completed: 'success', cancelled: 'danger' };

const inputCls = 'w-full rounded-xl border-2 border-secondary-200 px-3.5 py-2.5 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none';

const emptyForm = { leadName: '', leadEmail: '', leadPhone: '', company: '', preferredDate: '', notes: '' };

const PartnerScheduleMeeting = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ ...emptyForm });

  const { data, isLoading } = useQuery({
    queryKey: ['partner-meeting-requests'],
    queryFn: () => channelPartnerAuthAPI.getMeetingRequests(),
  });
  const requests = data?.data?.data || [];

  const createMutation = useMutation({
    mutationFn: (payload) => channelPartnerAuthAPI.createMeetingRequest(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['partner-meeting-requests']);
      toast.success('Meeting request sent — our team will reach out to schedule it');
      setForm({ ...emptyForm });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to submit request'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.leadName.trim() || !form.leadPhone.trim()) return;
    createMutation.mutate(form);
  };

  return (
    <div>
      <PageHeader
        title="Schedule Meeting"
        description="Have a hot lead? Request a call — our team handles the meeting itself"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-4">Request a Meeting</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-secondary-700">Lead Name *</label>
                <input type="text" value={form.leadName} onChange={(e) => setForm({ ...form, leadName: e.target.value })} className={inputCls} required />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-secondary-700">Phone *</label>
                <input type="text" value={form.leadPhone} onChange={(e) => setForm({ ...form, leadPhone: e.target.value })} className={inputCls} required />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-secondary-700">Email</label>
                <input type="email" value={form.leadEmail} onChange={(e) => setForm({ ...form, leadEmail: e.target.value })} className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-secondary-700">Company</label>
                <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-secondary-700">Preferred Date</label>
              <input type="date" value={form.preferredDate} onChange={(e) => setForm({ ...form, preferredDate: e.target.value })} className={inputCls} min={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-secondary-700">Notes</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything our team should know before the call" className={inputCls} />
            </div>
            <Button type="submit" variant="primary" loading={createMutation.isPending} disabled={createMutation.isPending}>
              Request Meeting
            </Button>
          </form>
        </Card>

        <Card>
          <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-4">Your Requests</p>
          {isLoading ? (
            <div className="flex items-center justify-center py-8"><Spinner /></div>
          ) : requests.length === 0 ? (
            <EmptyState
              icon={<HiOutlineCalendar className="w-7 h-7 text-secondary-400" />}
              title="No requests yet"
              description="Submitted meeting requests will show up here with their status."
            />
          ) : (
            <div className="space-y-3 max-h-[28rem] overflow-y-auto">
              {requests.map((r) => (
                <div key={r._id} className="rounded-xl border border-secondary-100 bg-secondary-50 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-secondary-900 text-sm truncate">{r.leadName}</p>
                      {r.company && <p className="text-xs text-secondary-400">{r.company}</p>}
                    </div>
                    <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                  </div>
                  <p className="text-xs text-secondary-500 mt-2 flex items-center gap-1.5">
                    <HiOutlinePhone className="w-3.5 h-3.5" /> {r.leadPhone}
                  </p>
                  {r.preferredDate && (
                    <p className="text-xs text-secondary-400 mt-1">Preferred: {formatDate(r.preferredDate)}</p>
                  )}
                  {r.status === 'scheduled' && r.scheduledAt && (
                    <p className="text-xs font-semibold text-primary-600 mt-1">Scheduled for {formatDate(r.scheduledAt)}</p>
                  )}
                  {r.superadminNotes && (
                    <p className="text-xs text-secondary-500 mt-1.5 italic">"{r.superadminNotes}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default PartnerScheduleMeeting;
